/**
 * PanoBar — Interior panorama variant selector.
 *
 * Shown at the bottom of the screen when cameraMode === 'interior',
 * replacing the MenuBar. Renders a horizontally-scrolling row of
 * rounded-square tiles (one per pano variant), styled consistently
 * with ColorSubMenu / RimSubMenu: white outline ring on selection,
 * fade masks at edges, label below.
 *
 * On tap:
 *   1. Store: setSelectedPano(id)
 *   2. Engine: showPano(url)
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md
 */
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useAppStore } from '../../store/app.store';
import { useUiTheme } from '../../hooks/useUiTheme';
import { tracker, EVENTS } from '../../services/tracking';
import type { ResolvedConfig } from '@ims/shared';
import type { Engine } from '@ims/engine';

interface PanoBarProps {
  config: ResolvedConfig | null;
  engineRef: React.RefObject<Engine | null>;
  visible: boolean;
}

export function PanoBar({ config, engineRef, visible }: PanoBarProps) {
  const selectedPanoId = useAppStore((s) => s.selectedPanoId);
  const setSelectedPano = useAppStore((s) => s.setSelectedPano);
  const sceneReady = useAppStore((s) => s.sceneReady);
  const theme = useUiTheme();

  const variants = useMemo(() => config?.panoVariants ?? [], [config]);
  const defaultId = variants[0]?.id ?? null;
  const activeId = selectedPanoId ?? defaultId;

  // Delay reveal until after the engine's black-curtain fade-out + load + fade-in.
  // Engine FADE_DURATION = 0.3s × 2 phases = 600ms + 300ms buffer = 900ms.
  // On exit we hide immediately — the curtain covers the transition.
  const [panoReady, setPanoReady] = useState(false);
  useEffect(() => {
    if (!visible) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset on exit, not derived state
      setPanoReady(false);
      return;
    }
    const t = setTimeout(() => setPanoReady(true), 900);
    return () => clearTimeout(t);
  }, [visible]);

  const [nameFadeKey, setNameFadeKey] = useState(0);
  const [displayName, setDisplayName] = useState<string>('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep displayName in sync with active variant, including on initial render
  useEffect(() => {
    const v = variants.find((v) => v.id === activeId);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronise display name with active variant
    setDisplayName(v?.displayName ?? v?.label ?? '');
  }, [activeId, variants]);

  const handleSelect = useCallback(
    (id: string) => {
      if (id === activeId) return;
      const variant = variants.find((v) => v.id === id);
      if (!variant) return;
      setSelectedPano(id);
      tracker.track(EVENTS.INTERIOR_CHANGED, { interiorId: id });
      setNameFadeKey((k) => k + 1);
      setDisplayName(variant.displayName ?? variant.label ?? '');
      // Swap pano in engine
      const engine = engineRef.current;
      if (engine) engine.showPano(variant.url);
    },
    [activeId, variants, setSelectedPano, engineRef],
  );

  if (!variants.length) return null;

  return (
    <div
      className="absolute left-0 right-0 bottom-0 z-100"
      style={{
        opacity: panoReady && sceneReady ? 1 : 0,
        pointerEvents: panoReady && sceneReady ? 'auto' : 'none',
        transition: 'opacity 400ms ease',
      }}
    >
      {/* Gradient backdrop — same as SubMenuPanel */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: theme.panelGradient }}
      />
      <div className="relative flex flex-col items-center pb-5 pt-3">
        {/* ── Tile scroll track ─────────────────────────────────────────── */}
        <div
          className="relative w-full"
          style={{
            maskImage:
              'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
          }}
        >
          <div
            ref={scrollRef}
            className="flex items-center gap-5 overflow-x-auto scrollbar-hide w-full py-1 px-1"
            style={{
              justifyContent: 'safe center',
              WebkitOverflowScrolling: 'touch' as React.CSSProperties['WebkitOverflowScrolling'],
              overscrollBehaviorX: 'contain',
            }}
          >
            {/* Leading spacer */}
            <div className="shrink-0 w-3" aria-hidden />

            {variants.map((variant) => {
              const isActive = variant.id === activeId;
              return (
                <button
                  key={variant.id}
                  onClick={() => handleSelect(variant.id)}
                  aria-label={variant.displayName ?? variant.label ?? variant.id}
                  aria-pressed={isActive}
                  className="relative shrink-0 cursor-pointer transition-all duration-200"
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: 10,
                    overflow: 'hidden',
                    background: '#1a1a1a',
                    boxShadow: isActive
                      ? '0 0 8px rgba(255,255,255,0.4), 0 2px_8px rgba(0,0,0,0.5)'
                      : '0 2px 8px rgba(0,0,0,0.5)',
                    outline: isActive ? '2px solid rgba(255,255,255,0.85)' : 'none',
                    outlineOffset: isActive ? 2 : 0,
                  }}
                >
                  {variant.thumbnail ? (
                    <img
                      src={variant.thumbnail}
                      alt=""
                      aria-hidden
                      draggable={false}
                      className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
                      style={{ borderRadius: 10 }}
                    />
                  ) : (
                    /* Fallback: camera icon */
                    <svg
                      aria-hidden
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className="absolute inset-0 w-full h-full p-4 text-white/50"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  )}
                </button>
              );
            })}

            {/* Trailing spacer */}
            <div className="shrink-0 w-3" aria-hidden />
          </div>
        </div>

        {/* ── Active variant label ────────────────────────────────────────── */}
        <p
          key={nameFadeKey}
          className="text-sm font-normal text-white text-center tracking-wide mt-3"
          style={{ animation: 'panoLabelFadeIn 200ms ease forwards' }}
        >
          {displayName}
        </p>
      </div>

      <style>{`
        @keyframes panoLabelFadeIn {
          from { opacity: 0; transform: translateY(3px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
